import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { bucketConfig } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import { isAfter, isDate } from 'date-fns';
import { isAxiosQueryKey } from 'app/agriculture/queries/types/AxiosQueryKey';
import { SnapshotBucketQueryKey } from 'app/agriculture/queries/snapshots/types/SnapshotBucketQueryKey';
import { SnapshotsMap } from 'app/agriculture/queries/snapshots/types/SnapshotsMap';

export function useSnapshotCacheInvalidation<
    T extends SnapshotType,
    I extends SnapshotInterface<T>,
>(snapshotType: T) {
    const queryClient = useQueryClient();
    const farm = useSelectedFarm();

    const addSnapshotToCache = useCallback(
        (snapshot: I) => {
            const { bucketSize } = snapshotConfig[snapshotType];
            const { startOfBucket } = bucketConfig[bucketSize];
            const bucketStart = startOfBucket(snapshot.timestamp);

            const queryKey: SnapshotBucketQueryKey<T> = [
                snapshotType,
                { farmId: farm.id, bucketTimestamp: bucketStart },
            ];

            queryClient.setQueryData<SnapshotsMap<I>>(queryKey, (snapshots) => {
                if (!snapshots) return;
                const newMap = new Map(snapshots);
                newMap.set(snapshot.parentId, [
                    ...(snapshots.get(snapshot.parentId) ?? []),
                    snapshot,
                ]);
                return newMap;
            });
        },
        [farm, queryClient, snapshotType],
    );

    const removeSnapshotsFromCache = useCallback(
        (parentId: I['parentId']) => {
            queryClient.setQueriesData<SnapshotsMap<I>>(
                {
                    queryKey: [
                        snapshotType,
                        {
                            farmId: farm.id,
                        },
                    ],
                    exact: false,
                },
                (snapshots) => {
                    if (!snapshots) return;
                    const newMap = new Map(snapshots);
                    newMap.delete(parentId);
                    return newMap;
                },
            );
        },
        [snapshotType, queryClient, farm.id],
    );

    const invalidateLatestBefore = useCallback(
        async (thresholdTimestamp: Date) => {
            await queryClient.invalidateQueries({
                queryKey: [
                    snapshotType,
                    {
                        farmId: farm.id,
                        latestBefore: true,
                    },
                ],
                exact: false,
                predicate: ({ queryKey }) => {
                    if (!isAxiosQueryKey(queryKey)) return false;
                    const { bucketTimestamp } = queryKey[1] ?? {};
                    if (!isDate(bucketTimestamp)) return false;
                    return isAfter(bucketTimestamp, thresholdTimestamp);
                },
            });
        },
        [snapshotType, queryClient, farm.id],
    );

    return {
        addSnapshotToCache,
        removeSnapshotsFromCache,
        invalidateLatestBefore,
    };
}
